import { useState, useEffect } from "react";
import { useCartData } from "@/zustand/cartStore.js";
import { useProductData } from "@/zustand/productStore.js";
import { currencyParser } from "@/func/productCardHelpers.js";
import { formatPrice } from "@/func/priceNotation";
import parse from "html-react-parser";

import shippingIconDefault from "@/assets/icons/shipping-icon.svg";
import DoubleTagIcon from "@/public/fallbacks/DoubleTagIcon.svg";
import { ChevronDownIcon } from "@/utils/miscIcons.jsx";

const resolveIcon = (value, fallback) => {
  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }
  if (value && typeof value === "object" && typeof value.url === "string") {
    return value.url;
  }
  return fallback;
};

function PremiumShippingUpsell() {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isMobileLayout, setIsMobileLayout] = useState(false);

  const { premiumShippingUpsellEnabled, setPremiumShippingUpsellEnabled, setSelectedProduct, selectedProduct } =
    useCartData();
  const { products, currency_code: currencyCode, formatted_string: formattedString } = useProductData();

  const settings = window.post_template?.checkout_v4_premium_shipping || {};
  const heading = settings.tr_checkout_v4_premium_shipping_heading || "";
  const title = settings.tr_checkout_v4_premium_shipping_title || "";
  const discountLabel = settings.tr_checkout_v4_premium_shipping_discount_label || "";
  const oldPrice = settings.tr_checkout_v4_premium_shipping_old_price || "";
  const priceValue = settings.checkout_v4_premium_shipping_price || "";
  const priceNumber = Number.parseFloat(priceValue);
  const price = Number.isNaN(priceNumber) ? 0 : priceNumber;
  const detailsHtml = settings.tr_checkout_v4_premium_shipping_details || "";
  const showMoreLabel = settings.tr_checkout_v4_premium_shipping_show_more_label || "";
  const showLessLabel = settings.tr_checkout_v4_premium_shipping_show_less_label || "";

  const renderedDetails = detailsHtml ? parse(detailsHtml) : null;

  const formattedOldPrice = oldPrice && formattedString ? formatPrice(oldPrice, formattedString) : "";

  const priceForDisplay = Number.isNaN(priceNumber) ? "0.00" : priceNumber.toFixed(2);
  const priceObject = formattedString ? currencyParser(priceForDisplay, currencyCode, formattedString) : null;

  const shippingIcon = resolveIcon(settings.checkout_v4_premium_shipping_icon_shipping, shippingIconDefault);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileLayout(window.innerWidth <= 992);
    };

    checkScreenSize();

    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const toggleSwitch = () => {
    setPremiumShippingUpsellEnabled(!premiumShippingUpsellEnabled);
  };

  useEffect(() => {
    if (!selectedProduct || !products?.length) return;

    // Find the "base" product by amount so we don't compound shipping
    // when users switch variants or product packs.
    const baseProduct = products.find((p) => p.amount === selectedProduct.amount);
    if (!baseProduct) return;

    // Normalize shipping values (ACF/API sends strings).
    const baseShipping = Number(baseProduct.shipping_price);
    const basePromoShipping = baseProduct.promo_shipping_price;

    const normalizedBaseShipping = Number.isFinite(baseShipping) ? baseShipping : 0;
    const normalizedBasePromoShipping = Number.isFinite(Number(basePromoShipping)) ? Number(basePromoShipping) : null;

    const targetShipping = premiumShippingUpsellEnabled ? normalizedBaseShipping + price : normalizedBaseShipping;
    const nextShipping = targetShipping.toFixed(2);

    const updates = {};

    if (String(selectedProduct.shipping_price ?? "") !== nextShipping) {
      updates.shipping_price = nextShipping;
    }

    if (normalizedBasePromoShipping !== null) {
      const targetPromo = premiumShippingUpsellEnabled
        ? normalizedBasePromoShipping + price
        : normalizedBasePromoShipping;
      const nextPromo = targetPromo.toFixed(2);
      if (String(selectedProduct.promo_shipping_price ?? "") !== nextPromo) {
        updates.promo_shipping_price = nextPromo;
      }
    }

    if (Object.keys(updates).length > 0) {
      setSelectedProduct({
        ...selectedProduct,
        ...updates,
      });
    }
  }, [premiumShippingUpsellEnabled, price, selectedProduct, products, setSelectedProduct]);

  return (
    <section className="premiumShippingUpsell">
      <h4 className="heading">{heading}</h4>

      <div className="card">
        <label className="option">
          <div className="iconRow">
            <input
              type="checkbox"
              className="checkbox"
              aria-label={title}
              checked={premiumShippingUpsellEnabled}
              onChange={toggleSwitch}
            />
            <div className="iconBox">
              <img src={shippingIcon} alt="" className="shippingIcon" />
            </div>
          </div>
          {/* Desktop  */}
          {!isMobileLayout && (
            <>
              <div className="labelColumn">
                <span className="labelText">{title}</span>
              </div>
              <div className="divider" />

              <div className="discountColumn">
                <div className="discountBadge">
                  <img src={DoubleTagIcon} alt="" className="discountIcon" />
                  <span className="discountText">{discountLabel}</span>
                </div>
              </div>
              <div className="divider" />
            </>
          )}
          {/* End Desktop  */}
          {/* Mobile  */}
          {isMobileLayout && (
            <>
              <div className="discountColumn">
                <div className="labelColumn">
                  <span className="labelText">{title}</span>
                </div>

                <div className="discountBadge">
                  <img src={DoubleTagIcon} alt="" className="discountIcon" />
                  <span className="discountText">{discountLabel}</span>
                </div>
              </div>
              <div className="divider" />
            </>
          )}
          {/* End Mobile  */}
          <div className="priceColumn">
            {formattedOldPrice && <span className="originalPrice">{formattedOldPrice}</span>}
            <span className="discountedPrice">
              {priceObject?.symbolLeft && <sup className="symbol">{priceObject?.symbol}</sup>}
              <span className="priceNumber">{priceObject?.number}</span>
              {priceObject?.hasCents && (
                <sup className="cents">
                  {priceObject?.decimalSeparator}
                  {priceObject?.cents}
                </sup>
              )}
              {!priceObject?.symbolLeft && priceObject?.symbol && <sup className="symbol">{priceObject?.symbol}</sup>}
            </span>
          </div>
        </label>

        <div className="horizontalDivider" />

        <div className="detailsContainer">
          {!isCollapsed && <div className="detailsContent">{renderedDetails}</div>}
        </div>

        <div className="collapseToggle" onClick={() => setIsCollapsed(!isCollapsed)} role="button" tabIndex={0}>
          <span>{isCollapsed ? showMoreLabel : showLessLabel}</span>
          <ChevronDownIcon className={isCollapsed ? "" : "iconRotated"} />
        </div>
      </div>
    </section>
  );
}

export default PremiumShippingUpsell;
